// Starting Publish's workers in the browser. The page requires Trusted Types for scripts, and a worker's URL is a
// script sink, so both workers start through one policy, `writing-suite-typst` (the name the CSP lists besides the
// service worker's): it admits exactly the two worker scripts this build made, and nothing else.
import { Typst, browserSpawn, fetchFonts, type ScriptURLPolicy } from '@writing-suite/typst';
import type { HostConnection, TypstClient } from '../store/publish.ts';
import type { FromHost, ToHost } from './protocol.ts';

declare const __PUBLISH_WORKER__: string;
declare const __REVISE_WORKER__: string;
declare const __TYPST__: { worker: string; glue: string; wasm: string; fonts: string } | null;

/** This build's Typst files (null: built with SKIP_TYPST=1, so no PDF). */
export const TYPST_ASSETS = typeof __TYPST__ === 'undefined' ? null : __TYPST__;
const PUBLISH_WORKER = typeof __PUBLISH_WORKER__ === 'undefined' ? '' : __PUBLISH_WORKER__;
/** The Revise room's worker (History's comparison, the style check: store/reviseHost.ts), started through the same policy. */
const REVISE_WORKER = typeof __REVISE_WORKER__ === 'undefined' ? '' : __REVISE_WORKER__;

interface TrustedTypePolicyFactory {
  createPolicy(name: string, rules: { createScriptURL(url: string): string }): ScriptURLPolicy;
}

let policy: ScriptURLPolicy | null | undefined;

/** The policy, made once per page (a policy name can be made only once). Null where Trusted Types don't exist. */
function workerPolicy(): ScriptURLPolicy | null {
  if (policy !== undefined) return policy;
  const tt = (window as unknown as { trustedTypes?: TrustedTypePolicyFactory }).trustedTypes;
  const allowed = new Set([PUBLISH_WORKER, REVISE_WORKER, TYPST_ASSETS?.worker].filter((u): u is string => !!u).map((u) => new URL(u, location.origin).href));
  policy = tt
    ? tt.createPolicy('writing-suite-typst', {
        createScriptURL: (u) => {
          const href = new URL(u, location.href).href;
          if (!allowed.has(href)) throw new TypeError(`not one of Publish's workers: ${u}`);
          return href;
        },
      })
    : null;
  return policy;
}

/** Starts the compile worker. */
export function connectCompileWorker(): HostConnection {
  const p = workerPolicy();
  const url = new URL(PUBLISH_WORKER, location.origin).href;
  const worker = new Worker((p ? p.createScriptURL(url) : url) as string, { type: 'module', name: 'publish' });
  return {
    port: {
      post: (message: ToHost, transfer?: Transferable[]) => worker.postMessage(message, transfer ?? []),
      listen: (fn: (m: FromHost) => void) => {
        worker.onmessage = (e: MessageEvent<FromHost>) => fn(e.data);
      },
    },
    terminate: () => worker.terminate(),
  };
}

/** Starts the Revise room's worker; null when this build has none (tests; the page then does the work itself). */
export function startReviseWorker(): Worker | null {
  if (!REVISE_WORKER || typeof Worker === 'undefined') return null;
  const p = workerPolicy();
  const url = new URL(REVISE_WORKER, location.origin).href;
  return new Worker((p ? p.createScriptURL(url) : url) as string, { type: 'module', name: 'revise' });
}

/** A factory for the Typst worker (null when this build has no PDF). Fonts are kept in Cache Storage once fetched. */
export function typstFactory(): (() => TypstClient) | null {
  const assets = TYPST_ASSETS;
  if (!assets) return null;
  return () => {
    const p = workerPolicy();
    return new Typst({
      spawn: browserSpawn(assets.worker, p ?? 'writing-suite-typst'),
      loadFont: typstFonts(),
      glue: new URL(assets.glue, location.origin).href,
      wasm: new URL(assets.wasm, location.origin).href,
    });
  };
}

let fonts: ((file: string) => Promise<Uint8Array>) | null = null;

/** The Typst fonts (for the engine, and for drawing its pages in the preview). */
export function typstFonts(): (file: string) => Promise<Uint8Array> {
  if (!TYPST_ASSETS) return () => Promise.reject(new Error('PDF isn’t in this build.'));
  fonts ??= cachedLoader(fetchFonts(new URL(TYPST_ASSETS.fonts, location.origin)));
  return fonts;
}

function cachedLoader(load: (file: string) => Promise<Uint8Array>): (file: string) => Promise<Uint8Array> {
  const cache = new Map<string, Promise<Uint8Array>>();
  return (file) => {
    let p = cache.get(file);
    if (!p) {
      p = load(file);
      cache.set(file, p);
      p.catch(() => cache.delete(file));
    }
    // Each caller gets its own copy: the engine's are sent to its worker.
    return p.then((b) => b.slice());
  };
}
